﻿/** @jsxImportSource solid-js */

import { Component, JSXElement, Show } from 'solid-js';
import Spinner from '../Spinner/Spinner';
import Button from '../Button/Button';
import { BtnClasses } from '../../types/common';

export type ButtonType = 'primary' | 'upgrade';

export interface RestrictedAccessSplashScreenProps {
  title?: string;
  subHeading?: string;
  description?: string;
  showIcon?: boolean;
  linkText?: string;
  linkUrl?: string;
  buttonLabel?: string;
  showBackgroundImage?: boolean;
  buttonType?: ButtonType;
  onButtonClick?(): void;
  onLinkClick?(): void;
  isLoading?: boolean;
  content?: JSXElement; // For additional content if needed
}

const RestrictedAccessSplashScreen: Component<
  RestrictedAccessSplashScreenProps
> = (props): JSXElement => {
  // Reactive getters for props
  const getTitle = (): string => props.title || '';
  const getSubHeading = (): string => props.subHeading || '';
  const getDescription = (): string => props.description || '';
  const getShowIcon = (): boolean => props.showIcon || false;
  const getLinkText = (): string => props.linkText || '';
  const getLinkUrl = (): string => props.linkUrl || '';
  const getButtonLabel = (): string => props.buttonLabel || '';
  const getShowBackgroundImage = (): boolean =>
    props.showBackgroundImage || false;
  const getButtonType = (): ButtonType => props.buttonType || 'primary';
  const getIsLoading = (): boolean => props.isLoading || false;

  // Asset paths - using relative paths since assets are in public folder
  const getImgSrc = (): string => '/assets/img/free-version.png';
  const getApiFailureImage = (): string => '/assets/img/bg_landscape.png';

  // Background style computation
  const getBackgroundStyle = (): Record<string, string> => {
    if (getShowBackgroundImage()) {
      return {
        'background-image': `url(${getApiFailureImage()})`,
      };
    }
    return {};
  };

  // Content transform style computation
  const getContentStyle = (): Record<string, string> => {
    if (getShowBackgroundImage()) {
      return {
        transform: 'translateY(-50%)',
      };
    }
    return {};
  };

  // Button color mapping
  const getButtonColor = (): BtnClasses => {
    return getButtonType() === 'primary'
      ? BtnClasses.Primary
      : BtnClasses.Tertiary;
  };

  // Event handlers
  const handleButtonClick = (): void => {
    props.onButtonClick?.();
  };

  const handleLinkClick = (): void => {
    props.onLinkClick?.();
  };

  return (
    <Show when={!getIsLoading()} fallback={<Spinner show={true} />}>
      <div class="splash-screen-container" style={getBackgroundStyle()}>
        <Show when={getShowIcon()}>
          <img src={getImgSrc()} alt="Lock Icon" class="popup-icon" />
        </Show>

        <div style={getContentStyle()}>
          <div class="splash-screen-header">
            <Show when={getTitle()}>
              <h1 class="heading">{getTitle()}</h1>
            </Show>
            <Show when={getSubHeading()}>
              <h2 class="sub-heading">{getSubHeading()}</h2>
            </Show>
          </div>

          <Show when={getDescription()}>
            <div class="splash-screen-body">
              <p>{getDescription()}</p>
            </div>
          </Show>

          <div class="splash-screen-footer mt-2">
            <Show when={getButtonLabel()}>
              <Button
                content={getButtonLabel()}
                color={getButtonColor()}
                type="solid"
                onClick={handleButtonClick}
                class="mt-1"
              />
            </Show>

            <Show when={getLinkText()}>
              <a
                href={getLinkUrl()}
                class="popup-link"
                rel="noopener noreferrer"
                onClick={handleLinkClick}
              >
                {getLinkText()}
              </a>
            </Show>
          </div>
        </div>

        {/* Render additional content if provided */}
        <Show when={props.content}>{props.content}</Show>
      </div>
    </Show>
  );
};

export default RestrictedAccessSplashScreen;
